import { Button } from '@compass/components'
import { useDateRangeContext } from 'features/commonUI'
import { buildTimelineCsvRows, TimelineExportPoint } from 'helpers/chartExport/timelineCsv'
import { useChartCsvExport, useIsChartCsvExportEnabled } from 'hooks/useChartCsvExport'
import { DownloadIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { ChartAggregation } from 'models'

export type TimelineExportButtonProps = {
  page: string
  widget: string
  granularity?: ChartAggregation
  columns: string[]
  getPoints: () => TimelineExportPoint[]
  hasData?: boolean
  filtersApplied?: boolean
  /** Overrides for pages whose effective range differs from the raw picker selection. */
  from?: DateTime
  to?: DateTime
  /** Zone the response buckets are aligned to, when it is not the user's (e.g. UTC-bucketed endpoints). */
  timeZone?: string
}

export function TimelineExportButton({
  page,
  widget,
  granularity,
  columns,
  getPoints,
  hasData = true,
  filtersApplied,
  from,
  to,
  timeZone: bucketTimeZone,
}: TimelineExportButtonProps) {
  const isEnabled = useIsChartCsvExportEnabled()
  const exportCsv = useChartCsvExport()
  const { dateRange, today, timeZone } = useDateRangeContext()

  const rangeStart = from ?? dateRange?.startDate
  const rangeEnd = to ?? dateRange?.endDate

  const handleExport = () => {
    if (granularity == null) {
      return
    }

    const rows = buildTimelineCsvRows({
      columns,
      points: getPoints(),
      granularity,
      timeZone: bucketTimeZone ?? timeZone,
      from: rangeStart,
      to: rangeEnd,
      now: today,
    })

    exportCsv({
      page,
      widget,
      qualifier: granularity,
      widgetType: 'timeline',
      rows,
      dataRowCount: rows.length - 1,
      filtersApplied,
      from: rangeStart,
      to: rangeEnd,
    })
  }

  if (!isEnabled) {
    return null
  }

  const isExportDisabled = !hasData || granularity == null

  return (
    <Button
      variant='secondary'
      size='sm'
      isIcon
      isDisabled={isExportDisabled}
      onPress={handleExport}
      aria-label={isExportDisabled ? 'Export unavailable' : `Export ${widget} as CSV`}
      tooltip={isExportDisabled ? undefined : 'Export as CSV'}
      data-testid={`chart-export-${page}-${widget}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
    >
      <DownloadIcon />
    </Button>
  )
}
